import type { MachineId, TokenSpec } from '../shared/contract.js';
import type { Modifier } from './modifier.js';
import type { Sim } from './sim.js';
import { v3 } from './vec.js';

/**
 * DIE LEHRNACHT — the world side of the tutorial.
 *
 * *Lehrgeld zahlen*: to pay your dues, to learn the expensive way. This file is the half
 * of that lesson core owns — **what the arcade looks like at each beat**, and nothing
 * else. The Croupier's voice, the printed slips, the prompts, the gate on the player's
 * hands and the eight-minute clock all live in `app/lehrnacht.ts`, because none of them
 * changes the world and therefore none of them belongs in a deterministic sim.
 *
 * Three rules govern everything below, and they are the reason this file is short:
 *
 * 1. **No special cases in any machine.** Nothing here is imported by `core/machines/*`
 *    and nothing here is known to them. Every effect is expressed in the vocabulary the
 *    House already has: which cabinets are switched on, what the claw holds, what is on
 *    the Hausregeln board, where the coins are lying. The covenant lint stays green
 *    because there is nothing here for it to object to.
 *
 * 2. **Every change arrives as a logged input.** `applyBeat` is only ever called from
 *    `RunDirector.meta` handling a `{t:'lehrbeat'}` verb, which the shell pushed through
 *    `RunDirector.push` like any other commitment. So the Lehrnacht is (seed + input log)
 *    exactly as every other run is, and replaying the log replays the lesson.
 *
 * 3. **Nothing is faked that the game would not do anyway.** The claw grips because a
 *    Hausregel on the board says it grips tonight — an ordinary `Modifier` answering the
 *    ordinary `odds` query, printed, attributable, and inspectable with the odds
 *    inspector like every other rule in the game. The pot fills because coins really are
 *    teetering at the lip. A tutorial that lies about its own rules is teaching the wrong
 *    game, and this one is inspectable to the same depth as the real thing.
 */

/**
 * **The night has a fixed seed.** Not because any single physical outcome is hunted for
 * — see DECISION in STATUS — but because a tutorial that shuffles its own furniture is a
 * tutorial nobody can debug, screenshot, or hold to a clock.
 */
export const LEHRNACHT_SEED = 1848;

/** Der Croupier's marker, and floor 1's debt: the same fifty. `DEBT_CURVE[0]`. */
export const LEHRNACHT_MARKER = 50;

/**
 * What he puts in your hand. Deliberately **the marker itself**: he hands you fifty and
 * wants fifty back at sunrise, so the arithmetic of the whole night is one number the
 * player can hold in their head. Every buy-in is visibly the House taking some of it
 * back, which is the joke the economy is built on.
 */
export const LEHRNACHT_COINS = 50;

/**
 * The one rule-changer on the Preistheke's floor, guaranteed.
 *
 * core-loop.md's opening gate is explicit that the first item is "never a token — a charm
 * that visibly warps the next machine". Spaltmünze is the most *visible* rule-change in
 * the game: the next coin you drop comes apart in mid-air, on screen, in front of you.
 * Zinseszins is the better item and the worse lesson — ripening is invisible for ten
 * seconds, and beat 3 has to land in one drop.
 */
export const LEHRNACHT_PRIZE = 'spaltmuenze';

/** The beats, in order. The shell's director walks this list; core only reacts. */
export const LEHR_BEATS = [
  'tuer',      // the doors close, the sign flickers, he takes your marker
  'wurf',      // 1 · der erste Wurf
  'kuenste',   // 2 · die dunklen Künste
  'zuviel',    //     …and the shove that is one too many
  'regel',     // 3 · die erste Regel
  'maulzu',    //     …and the mouth closes the instant it has done its job
  'wahl',      // 4 · kassieren oder nachlegen
  'lawine',    //     …the stroke, if they double down
  'schluss',   // 5 · die kleine Abrechnung
] as const;

export type LehrBeat = (typeof LEHR_BEATS)[number];

export const isLehrBeat = (s: string): s is LehrBeat =>
  (LEHR_BEATS as readonly string[]).includes(s);

/**
 * Tonight's house rules. There is exactly one, and it is a real rule on a real board.
 *
 * **Why a Hausregel instead of a scripted success.** Beat 3 must not be losable — a first
 * grab that slips is four wasted minutes and a player who has learned that the shop is a
 * slot machine. The lazy fix is an `if (tutorial) return true` inside the claw. The
 * grammar already has the honest fix: the grip is a declared, GLÜCK-bendable `odds`
 * question, and a modifier may answer it. So the House rigs the claw *in the player's
 * favour*, prints that it has done so on the Hausregeln board in the player's own
 * language, and the player can hold I and read the number. The first rule this game ever
 * shows you is a rule you can check — which is the entire thesis of the odds inspector.
 */
export const LEHRNACHT_RULES: readonly Modifier[] = Object.freeze([
  {
    id: 'lehrnacht:fester-griff',
    source: 'hausregel',
    tags: [],
    priority: 5,
    machines: ['greifer'] as MachineId[],
    ruleText: 'Heute greift der Greifer fest.',
    discovered: true,
    // The pipeline records provenance by itself whenever a modifier changes an answer
    // (see `Pipeline.query`), so this line lands on the Preistheke's board without being
    // told to. BASE_GRIP is 0.62, so it always changes the answer, so it always prints.
    queries: {
      odds: (ctx, p) => (ctx.stream === 'greifer' && ctx.event === 'grip' ? 1 : p),
    },
  } satisfies Modifier,
]);

/**
 * The one rule the House adds when the lesson reaches the Preistheke, and the reason
 * beat 3 lands in a single drop.
 *
 * [Spalt] splits **on jackpot hits only** — that is the tag's real contract, not a
 * detail. So handing a player a Spaltmünze and asking them to drop once teaches them
 * nothing at all seven times out of eight: the coin lands in an ordinary pocket, pays
 * ordinary money, and the "rule-changer" they were promised appears to do nothing. The
 * honest fix is not to weaken the tag, it is to make the jackpot mouth reachable — which
 * is a thing the House does to cabinets all the time, is expressible as an ordinary
 * `pocketWidth` query, prints itself on the board, and is *funny*: the Croupier has
 * opened the middle of the machine like a drain and says nothing about it.
 *
 * **It closes again the instant it has split one coin, and that is not tidiness — it is
 * the only thing that stops the tutorial exploding.**
 *
 * `splitToken` spawns both children **at the position of the score**, which for a jackpot
 * is the middle of the machine. They pop up and fall straight back into the mouth they
 * were born in. So while the mouth is wide, a split produces two coins that are very
 * likely to jackpot and split again: a branching process whose mean offspring count is
 * well above one, and which ends only when the cabinet bursts. Measured at ×5: the
 * pachinko GEBORSTEN, 490 Münzen against a 50-coin marker, VERDACHT 90, HAUSVERBOT, three
 * ZINKEN installed and Der Rausschmeißer taking a token — all inside the fifth minute of a
 * tutorial. Measured again at ×3: still burst, VERDACHT 55, 442 Münzen. Narrowing the
 * multiplier could not fix it, because **the width was never the driver; the birthplace of
 * the children was.**
 *
 * So the mouth is wide for exactly one hit. `applyBeat('maulzu')` is pushed the moment the
 * split is seen, the mouth returns to its ordinary size, and the two children fall into
 * the normal 14 %-wide jackpot pocket like anybody else's coins. One generation, and over.
 * The rule text says „einmal" because that is exactly what it does.
 *
 * (Why it needs widening at all: `./scripts/dev.sh run aim` measures where a pachinko ball
 * actually goes, and the peg field throws it toward the launcher's side — across a 50-shot
 * grid the TOPF was reached **once**. A beat that waits for an unassisted jackpot waits
 * all night.)
 */
export const LEHRNACHT_WIDE_MOUTH: Modifier = {
  id: 'lehrnacht:weites-maul',
  source: 'hausregel',
  tags: [],
  priority: 6,
  machines: ['pachinko'],
  ruleText: 'Einmal ist das mittlere Maul weit offen.',
  discovered: true,
  queries: {
    pocketWidth: (_ctx, v) => v * 3,
  },
};

/** The bag you are lent: ten plain coins, not one enchantment among them. */
export function lehrnachtBeutel(): TokenSpec[] {
  const out: TokenSpec[] = [];
  let id = 1;
  for (let i = 0; i < 7; i++) out.push({ id: id++, material: 'kupfer', faceValue: 1, radius: 0.5, tags: [] });
  for (let i = 0; i < 3; i++) out.push({ id: id++, material: 'silber', faceValue: 2, radius: 0.5, tags: [] });
  return out;
}

/** What the run director must be able to do for a beat, beyond touching the sim. */
export interface LehrHost {
  /** put exactly these item ids on the Preistheke's floor, as wrapped parcels */
  stockPreistheke(ids: readonly string[]): void;
}

/** Cabinets that exist in the aisle. The FLIPPER stands behind the Saalchef's door. */
const AISLE: readonly MachineId[] = ['pachinko', 'muenzschieber', 'bandit', 'roulette', 'greifer'];

/** Light exactly these; leave the rest of the arcade dark. */
function onlyLit(sim: Sim, lit: readonly MachineId[]): void {
  for (const id of AISLE) {
    const c = sim.cabinet(id);
    if (c) c.powered = lit.includes(id);
  }
}

/**
 * Apply one beat's world state. Idempotent on purpose: the shell re-pushes a beat when
 * the player idles long enough for a decaying meter to drift out of the shape the lesson
 * needs, and a beat that did something different the second time would make the tutorial
 * depend on how long somebody stared at it.
 */
export function applyBeat(sim: Sim, beat: LehrBeat, host: LehrHost): void {
  switch (beat) {
    /**
     * The doors close. Only the pachinko is lit — not as a lockout, but because a room
     * with one lit machine in it *tells you where to go* without a single arrow drawn on
     * the screen. The rest of the arcade is visibly there, visibly off, and visibly the
     * reason to come back.
     */
    case 'tuer':
      onlyLit(sim, ['pachinko']);
      break;

    case 'wurf':
    case 'kuenste':
      onlyLit(sim, ['pachinko']);
      break;

    /**
     * The shove that is one too many.
     *
     * TILT costs 0.22 a shove, so a meter starting at zero takes five. Five prompted
     * shoves is not a joke, it is a chore — and the lesson is the *raspberry*, not the
     * arithmetic. So the beat leaves the meter where an evening of shoving would have
     * left it: nearly full, on the gauge, where the player can see it before they touch
     * the glass. Nothing is hidden and no number is invented — one more shove really does
     * tilt a machine sitting at 0.99, in the Lehrnacht and in every other run.
     */
    case 'zuviel': {
      const c = sim.cabinet('pachinko');
      if (c) c.tilt01 = 0.99;
      break;
    }

    /**
     * The Preistheke wakes up with one parcel on its floor. One, because a claw cabinet
     * with a heap in it is a game about the heap; tonight it is a game about the idea
     * that a thing in a box can rewrite a machine you already understand.
     */
    case 'regel': {
      onlyLit(sim, ['pachinko', 'greifer']);
      ensureCanPlay(sim);
      host.stockPreistheke([LEHRNACHT_PRIZE]);
      sim.pipe.add(LEHRNACHT_WIDE_MOUTH);
      // He fronts you the grab. He wants his fifty back and you are not going to earn it
      // by standing there — which is the most in-character thing he does all night.
      if (sim.economy.tickets < GRAB_TICKETS) {
        sim.economy.addTickets(GRAB_TICKETS - sim.economy.tickets);
      }
      break;
    }

    /**
     * The coin pusher, with its pile standing at the lip.
     *
     * This is the one beat that puts coins on the shelf by hand, and it is worth saying
     * why that is honest rather than staged: a Münzschieber that has been played all
     * evening *has* an overhang, and the player is arriving at 3 a.m. at a machine other
     * people have been feeding. The alternative — waiting for the bar to build the
     * overhang naturally — is four minutes of watching a bar, inside an eight-minute
     * budget. STRÄHNE is at two for the same reason: they have just scored on the
     * pachinko twice, and the streak they are being asked to gamble has to be a streak
     * they can see on the cabinet.
     */
    /** The coin has come apart. The drain in the middle of the machine closes again. */
    case 'maulzu':
      sim.pipe.remove(LEHRNACHT_WIDE_MOUTH.id);
      break;

    case 'wahl': {
      onlyLit(sim, ['pachinko', 'greifer', 'muenzschieber']);
      ensureCanPlay(sim);
      // belt and braces: if they never split a coin, the mouth still closes here
      sim.pipe.remove(LEHRNACHT_WIDE_MOUTH.id);
      const c = sim.cabinet('muenzschieber');
      if (!c) break;
      c.straehne = Math.max(c.straehne, 2);
      seedLip(sim);
      break;
    }

    /**
     * The stroke. Only pushed if the player takes the bet.
     *
     * The design asks the seed to guarantee this. It does not, and it should not: see the
     * DECISION in STATUS. The pusher bar is what sends a poised overhang over the lip in
     * a real machine, and this is that stroke, applied to exactly the coins that are
     * already hanging over it. It is the same nudge `ruetteln` gives, aimed forwards.
     */
    case 'lawine':
      shoveLip(sim);
      break;

    case 'schluss':
      break;
  }
}

/** A grab costs this many tickets at the Preistheke (`greifer.ts`'s own constant). */
const GRAB_TICKETS = 6;

/**
 * Enough in the purse to walk into the next cabinet, guaranteed.
 *
 * Entering a machine costs `5 × stakeLevel × 1.6^entries`, which is route pressure and is
 * a good rule — in a *run*. In the Lehrnacht it is a soft-lock waiting to happen: a player
 * who wanders in and out of cabinets a dozen times pays a rising toll each time, and can
 * arrive at beat 4 unable to afford the machine the beat is about. Measured: the
 * contrarian bot did exactly that and stood in the aisle while the lesson waited for it to
 * come inside.
 *
 * So Der Croupier tops the purse up. He is not being kind — he is protecting his
 * investment, which is the only thing he has done all night, and a marker of fifty is
 * still a marker of fifty at sunrise.
 */
const CARFARE = 30;

/** He does not let his debtor get stranded in the aisle. */
function ensureCanPlay(sim: Sim): void {
  if (sim.economy.muenzen < CARFARE) sim.economy.addCoins(CARFARE - sim.economy.muenzen);
}

/** Shelf geometry, read off `muenzschieber.ts`. The lip is at local z ≈ -1. */
const LIP = {
  /** rows of coins standing in front of the resident pile (which starts at z ≈ 0.9) */
  rowsZ: [-0.9, -0.5, -0.1] as const,
  cols: 7,
  x0: -3.6,
  dx: 1.2,
  y: 0.16,
} as const;

/** Stand a wall of coins at the front lip, poised. Idempotent: never seeds twice. */
function seedLip(sim: Sim): void {
  const cab = sim.cabinet('muenzschieber');
  if (!cab) return;
  // Idempotence, and the reason it matters: the shell re-pushes `wahl` if the player
  // wanders off and comes back, and a second wall would double the payout and quietly
  // teach that loitering pays.
  if (countAtLip(sim) >= LIP.cols * 2) return;
  const o = cab.origin;
  for (const z of LIP.rowsZ) {
    for (let i = 0; i < LIP.cols; i++) {
      const x = LIP.x0 + i * LIP.dx;
      sim.spawnToken(
        { id: -1, material: 'kupfer', faceValue: 1, radius: 0.5, tags: [] },
        'muenzschieber',
        v3(o.x + x, o.y + LIP.y, o.z + z),
        v3(0, 0, 0),
      );
    }
  }
}

/** How many coins are already standing in the lip band. */
function countAtLip(sim: Sim): number {
  const cab = sim.cabinet('muenzschieber');
  if (!cab) return 0;
  let n = 0;
  for (const t of sim.tokensIn('muenzschieber')) {
    const p = sim.position(t.id);
    if (!p) continue;
    const z = p.z - cab.origin.z;
    if (z < 0.2 && z > -1.25) n++;
  }
  return n;
}

/**
 * The pusher's stroke, as an impulse on the coins that are over the lip.
 *
 * `AVALANCHE_COINS = 8` crossings inside one second is what makes the Münzschieber call
 * something a *Lawine* and pay it as jackpot-class, so the stroke has to move more than
 * eight coins at once or the lesson pays out as small change and the hook does not land.
 */
function shoveLip(sim: Sim): void {
  const cab = sim.cabinet('muenzschieber');
  if (!cab) return;
  for (const t of sim.tokensIn('muenzschieber')) {
    const p = sim.position(t.id);
    if (!p) continue;
    const z = p.z - cab.origin.z;
    if (z > 0.35 || z < -1.25) continue;
    // forward and very slightly up: a bar shoves, it does not flick
    sim.impulse(t.id, v3(0, 0.35, -2.6));
  }
}
